// components
import { Typography } from '@mui/material';
import DutyChip from 'components/shared/ToolbarSet/components/DutyChip';
import TooltipContent from './TooltipContent';

// hooks
import { useTranslation } from 'react-i18next';

// styles
import { StyledCapitalizedBox } from 'components/employee-card/employment/hours-table/components/TooltipCell.styles';

// types
import { QualificationResponse } from 'RestAPIs/roster/qualifications/types';

// utils
import dayjs from 'dayjs';
import { DutySignals } from 'components/shared/ToolbarSet/components/DutyChip.types';
import { CostCentersFromOthersPrefValue } from '../hooks/useGetColumns.types';

const DutyCell = ({
  params,
  date,
  isOpenRequest = false,
  qualificationsByCostCenters = [],
  isAbsentToday = false,
  costCenterFromOthersPrefValue
}: {
  params: any;
  date: string;
  isOpenRequest?: boolean;
  qualificationsByCostCenters?: QualificationResponse[];
  isAbsentToday?: boolean;
  costCenterFromOthersPrefValue?: CostCentersFromOthersPrefValue;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });
  const costCenterSourceShortName = params.row.customerNameShort || '—';
  const costCenterSourceCode = params.row.customerCode || '—';

  const showOtherCostCentersDuties =
    costCenterFromOthersPrefValue === 'show_with_box' ||
    costCenterFromOthersPrefValue === 'show_with_box_and_code';

  const rawDuties = isOpenRequest
    ? params.row.days?.filter((duty) =>
        dayjs(dayjs(duty.date).format('YYYY-MM-DD')).isSame(date, 'day')
      ) || []
    : params.row.days?.find((duty) => dayjs(duty.date).isSame(date, 'day'))
        ?.duties || [];

  const otherCostCentersDuties = showOtherCostCentersDuties
    ? params.row.days?.find((duty) => dayjs(duty.date).isSame(date, 'day'))
        ?.otherCostCentersDuties || []
    : [];

  const dutiesArray = [...rawDuties, ...otherCostCentersDuties];

  return dutiesArray?.length
    ? dutiesArray?.map((duty) => {
        const qualifications = qualificationsByCostCenters?.filter(
          (qualification) => qualification.requestId === duty.requestId
        );

        const tooltipDutyContent = [
          ...(isOpenRequest
            ? [
                {
                  label: t('dataGrid.tooltip.dutyCodeGeneral'),
                  value: duty.dutyCode
                },
                {
                  label: t('dataGrid.tooltip.dutyCodeCostCenter'),
                  value: duty.displayDutyCode
                }
              ]
            : [
                {
                  label: t('dataGrid.tooltip.status'),
                  value: duty.flags
                    ?.map((flag: string) =>
                      t(`dataGrid.tooltip.statusValue.${flag}`)
                    )
                    .join(', ')
                },
                {
                  label: t('dataGrid.tooltip.dutyCode'),
                  value: duty.displayDutyCode
                }
              ]),

          {
            label: t('dataGrid.tooltip.periods'),
            value:
              duty?.periods?.length > 0 &&
              (duty?.periods ?? []).map((period, index) => {
                const start = dayjs(period.startTime).format('HH:mm');
                const end = dayjs(period.endTime).format('HH:mm');
                return (
                  <StyledCapitalizedBox key={index}>
                    {`${start} - ${end}`} &middot;{' '}
                    {` ${period.dutyHourType?.description}`}
                  </StyledCapitalizedBox>
                );
              })
          },
          ...(qualifications?.length
            ? [
                {
                  label: t('dataGrid.tooltip.qualifications'),
                  value:
                    qualifications?.length > 0 ? (
                      qualifications.map((qualification, index) => (
                        <Typography key={index} variant="body2">
                          {qualification.name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )
                }
              ]
            : []),
          ...(isOpenRequest
            ? [
                {
                  label: t('dataGrid.tooltip.hours'),
                  value: duty.hoursForCalculation
                },
                ...(duty.preferredEmployees?.length
                  ? [
                      {
                        label: t('dataGrid.tooltip.preferredEmployees'),
                        value: duty.preferredEmployees
                          ?.sort(
                            (a, b) =>
                              a.employee?.nameCompound.localeCompare(
                                b.employee?.nameCompoundSurname
                              )
                          )
                          .map((preferredEmployee, index) => (
                            <Typography key={index} variant="body2">
                              {preferredEmployee.employee?.nameCompoundSurname}
                            </Typography>
                          ))
                      }
                    ]
                  : [])
              ]
            : [
                {
                  label: t('dataGrid.tooltip.source'),
                  value: `${costCenterSourceShortName} (${costCenterSourceCode})`
                },
                {
                  label: t('dataGrid.tooltip.commitment'),
                  value: `${duty.costCenterNameShort} (${duty.costCenterCode})`
                },
                {
                  label: t('dataGrid.tooltip.workAgreement'),
                  value: duty.workAgreementNr
                }
              ]),
          ...(duty.notes?.length
            ? [
                {
                  label: t('dataGrid.tooltip.notes'),
                  value: duty.notes.map((note, index) => (
                    <Typography key={index} variant="body2">
                      {note.content ?? note}
                    </Typography>
                  ))
                }
              ]
            : [])
        ];

        const signals: DutySignals[] = [
          {
            key: 'hasNotes',
            active: !!duty.notes?.length,
            icon: 'note'
          },
          {
            key: 'hasQualifications',
            active: qualifications.length > 0,
            icon: 'medal'
          },
          {
            key: 'hasPreferredEmployees',
            active: isOpenRequest ? !!duty.preferredEmployees?.length : false,
            icon: 'users'
          },
          {
            key: 'hasUpdatedDutyHourType',
            active: duty.flags?.includes('DHT_CHANGED'),
            icon: 'asterisk'
          },
          {
            key: 'isAiMatch',
            active: !!duty.isAutoMatch,
            icon: 'double-sparkle',
            alignBottom: true
          }
        ];

        return (
          <DutyChip
            key={duty.dutyLineId}
            isTableCell
            tooltipContent={
              <TooltipContent
                content={tooltipDutyContent}
                signals={signals}
                isAbsentToday={isAbsentToday}
              />
            }
            isAbsentToday={isAbsentToday}
            signals={signals}
            isAiMatch={!!duty.isAutoMatch}
            duties={[
              {
                code:
                  duty.isFromAnotherCostCenter &&
                  costCenterFromOthersPrefValue === 'show_with_box'
                    ? ''
                    : duty.displayDutyCode,
                color: duty.displayDutyColor ?? duty.displayDutyColorCode,
                isBlocked: duty.flags?.includes('STATUS_BLOCKED'),
                isFromAnotherCostCenter: !!duty.isFromAnotherCostCenter
              }
            ]}
          />
        );
      })
    : null;
};

export default DutyCell;
